import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import type { User } from '@supabase/supabase-js';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '../supabase';
import { AdminLayout } from '../AdminLayout';
import { formatDateDisplay } from '../../utils/date';
import { Modal } from '../../components/Modal';
import { trackEvent } from '../../utils/analytics';
import { SignupCalendar } from '../components/SignupCalendar';
import { SignupScheduleEditor } from '../components/SignupScheduleEditor';
import { SignupAudienceEditor } from '../components/SignupAudienceEditor';
import { SignupLockEditor } from '../components/SignupLockEditor';
import { DEFAULT_LOCK_HOURS, describeLockWindow, toLockColumn } from '../../utils/signupLock';
import {
  meetingModeLabel,
  normalizeMeetingMode,
  slotDraftsToRows,
  slotsFromSchedule,
  timeToMinutes,
  toHHMM,
  type SignupMeetingMode,
  type SignupScheduleValue,
  type SlotDraft,
} from '../../utils/signupSlots';
import { Calendar, Clock, Users, Trash2, ExternalLink, Edit, Copy, BarChart, Video, Power, PowerOff, Target, Lock } from 'lucide-react';

interface SignupEvent {
  id: string;
  title: string;
  description: string | null;
  start_date: string;
  end_date: string;
  slot_duration_minutes: number;
  max_signups_per_slot: number;
  is_active: boolean;
  created_at: string;
  updated_at: string;
  target_grades: string[] | null;
  /** NULL = parents can change a booking right up to the start. */
  lock_hours_before: number | null;
}

interface SignupSlot {
  id: string;
  event_id: string;
  slot_date: string;
  start_time: string;
  end_time: string;
  meeting_mode: SignupMeetingMode | null;
  is_available: boolean;
  created_at: string;
  signup_registrations: SignupRegistration[];
}

interface SignupRegistration {
  id: string;
  slot_id: string;
  parent_name: string;
  parent_email: string;
  student_name: string;
  phone: string | null;
  notes: string | null;
  created_at: string;
}



type EventFormData = SignupScheduleValue & {
  title: string;
  description: string;
  maxSignupsPerSlot: number;
  isActive: boolean;
  /** Empty means no restriction — the round is offered to every after-school family. */
  targetGrades: string[];
  /** Off is stored as NULL. The hours are kept while off so toggling back restores them. */
  lockEnabled: boolean;
  lockHoursBefore: number;
};

const EMPTY_EVENT_FORM: EventFormData = {
  title: '',
  description: '',
  startDate: '',
  endDate: '',
  slotDurationMinutes: 15,
  slotIntervalMinutes: 15,
  maxSignupsPerSlot: 1,
  scheduleMode: 'daily',
  morningStartTime: '09:30',
  morningEndTime: '11:30',
  morningMeetingMode: '',
  eveningStartTime: '17:30',
  eveningEndTime: '18:30',
  eveningMeetingMode: '',
  customSlots: [],
  isActive: true,
  targetGrades: [],
  // New rounds lock a day ahead; a round saved before the setting existed opens with it off.
  lockEnabled: true,
  lockHoursBefore: DEFAULT_LOCK_HOURS,
};

/** Empty arrays are stored as NULL, which is what "no restriction" means in the column. */
const toTargetColumn = (values: string[]): string[] | null => (values.length > 0 ? values : null);

export function AdminSignups() {
  const [currentUser, setCurrentUser] = useState<User | null>(null);
  useEffect(() => {
    void supabase.auth.getUser().then(({ data }) => setCurrentUser(data.user));
  }, []);

  const navigate = useNavigate();
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  // The edit modal loads the event's real slots asynchronously. Until that lands, the form
  // describes no slots at all — and "Regenerate" would happily delete every empty slot and
  // create nothing in their place.
  const [isLoadingEventSlots, setIsLoadingEventSlots] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showEditModal, setShowEditModal] = useState<string | null>(null);
  const [editingEventId, setEditingEventId] = useState<string | null>(null);
  const [includeInactiveEvents, setIncludeInactiveEvents] = useState(false); // default: show active only
  const [formData, setFormData] = useState<EventFormData>(EMPTY_EVENT_FORM);

  const patchForm = (patch: Partial<EventFormData>) => setFormData((prev) => ({ ...prev, ...patch }));
  const queryClient = useQueryClient();

  type SlotTimeRow = {
    slot_date: string;
    start_time: string;
    end_time: string;
    meeting_mode?: string | null;
  };

  const inferTimeBlocksFromDaySlots = (daySlots: SlotTimeRow[], slotDurationMinutes: number): {
    morningStartTime: string;
    morningEndTime: string;
    morningMeetingMode: SignupMeetingMode | '';
    eveningStartTime: string;
    eveningEndTime: string;
    eveningMeetingMode: SignupMeetingMode | '';
  } => {
    const empty = {
      morningStartTime: '',
      morningEndTime: '',
      morningMeetingMode: '' as SignupMeetingMode | '',
      eveningStartTime: '',
      eveningEndTime: '',
      eveningMeetingMode: '' as SignupMeetingMode | '',
    };
    if (daySlots.length === 0) return empty;

    const sorted = [...daySlots].sort((a, b) => timeToMinutes(a.start_time) - timeToMinutes(b.start_time));

    // Find the largest gap between consecutive slots. If it's big enough, treat it as morning/evening split.
    let splitIndex: number | null = null;
    let largestGap = 0;
    for (let i = 0; i < sorted.length - 1; i++) {
      const gap = timeToMinutes(sorted[i + 1].start_time) - timeToMinutes(sorted[i].start_time);
      if (gap > largestGap) {
        largestGap = gap;
        splitIndex = i;
      }
    }

    const shouldSplit = splitIndex !== null && largestGap >= slotDurationMinutes * 2;
    const morningGroup = shouldSplit ? sorted.slice(0, splitIndex! + 1) : sorted;
    const eveningGroup = shouldSplit ? sorted.slice(splitIndex! + 1) : [];

    return {
      morningStartTime: toHHMM(morningGroup[0].start_time),
      morningEndTime: toHHMM(morningGroup[morningGroup.length - 1].end_time),
      morningMeetingMode: normalizeMeetingMode(morningGroup[0].meeting_mode) ?? '',
      eveningStartTime: eveningGroup.length > 0 ? toHHMM(eveningGroup[0].start_time) : '',
      eveningEndTime: eveningGroup.length > 0 ? toHHMM(eveningGroup[eveningGroup.length - 1].end_time) : '',
      eveningMeetingMode: eveningGroup.length > 0 ? normalizeMeetingMode(eveningGroup[0].meeting_mode) ?? '' : '',
    };
  };

  const buildSlotsForEvent = (eventId: string, eventData: EventFormData) =>
    slotDraftsToRows(eventId, slotsFromSchedule(eventData));

  // Fetch signup events with registration counts
  const { data: events = [], isLoading: eventsLoading } = useQuery({
    queryKey: ['signup-events', includeInactiveEvents ? 'all' : 'active'],
    queryFn: async () => {
      let query = supabase
        .from('signup_events')
        .select(`
          *,
          signup_slots!inner (
            id,
            meeting_mode,
            signup_registrations (id)
          )
        `)
        .order('created_at', { ascending: false });

      if (!includeInactiveEvents) {
        query = query.eq('is_active', true);
      }

      const { data, error } = await query;
      
      if (error) throw error;
      
      // Transform data to include registration counts and which meeting modes it uses
      const eventsWithCounts = data.map(event => {
        const registrationCount = event.signup_slots?.reduce((total: number, slot: SignupSlot) => {
          return total + (slot.signup_registrations?.length || 0);
        }, 0) || 0;

        const meetingModes = [
          ...new Set(
            (event.signup_slots ?? [])
              .map((slot: SignupSlot) => normalizeMeetingMode(slot.meeting_mode))
              .filter((mode: SignupMeetingMode | null): mode is SignupMeetingMode => mode !== null)
          ),
        ];

        return {
          ...event,
          registration_count: registrationCount,
          meeting_modes: meetingModes,
        };
      });

      return eventsWithCounts as (SignupEvent & { registration_count: number; meeting_modes: SignupMeetingMode[] })[];
    },
  });



  // Create event mutation
  const createEventMutation = useMutation({
    mutationFn: async (eventData: EventFormData) => {
      // Create the event
      const { data: event, error: eventError } = await supabase
        .from('signup_events')
        .insert({
          title: eventData.title,
          description: eventData.description || null,
          start_date: eventData.startDate,
          end_date: eventData.endDate,
          slot_duration_minutes: eventData.slotDurationMinutes,
          max_signups_per_slot: eventData.maxSignupsPerSlot,
          is_active: eventData.isActive,
          target_grades: toTargetColumn(eventData.targetGrades),
          lock_hours_before: toLockColumn(eventData.lockEnabled, eventData.lockHoursBefore),
        })
        .select()
        .single();

      if (eventError) throw eventError;

      // Generate slots for the event
      const slots = buildSlotsForEvent(event.id, eventData);

      // Insert all slots
      if (slots.length > 0) {
        const { error: slotsError } = await supabase
          .from('signup_slots')
          .insert(slots);
        
        if (slotsError) throw slotsError;
      }

      return event;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signup-events'] });
      setShowCreateModal(false);
      setFormData(EMPTY_EVENT_FORM);
      trackEvent({
        eventType: 'admin_signup_event_create',
        user: currentUser,
        userType: 'admin',
        entityType: 'signup_events'
      });
    },
  });

  // Update event metadata mutation (does not touch slots)
  const updateEventMetadataMutation = useMutation({
    mutationFn: async ({ eventId, eventData }: { eventId: string; eventData: EventFormData }) => {
      // Update event metadata only; slot regeneration is a separate action
      const { error: eventError } = await supabase
        .from('signup_events')
        .update({
          title: eventData.title,
          description: eventData.description || null,
          start_date: eventData.startDate,
          end_date: eventData.endDate,
          slot_duration_minutes: eventData.slotDurationMinutes,
          max_signups_per_slot: eventData.maxSignupsPerSlot,
          is_active: eventData.isActive,
          target_grades: toTargetColumn(eventData.targetGrades),
          lock_hours_before: toLockColumn(eventData.lockEnabled, eventData.lockHoursBefore),
        })
        .eq('id', eventId);

      if (eventError) throw eventError;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signup-events'] });
      trackEvent({
        eventType: 'admin_signup_event_edit',
        user: currentUser,
        userType: 'admin',
        entityType: 'signup_events'
      });
    },
  });

  // Regenerate slots mutation (preserves slots with registrations)
  const regenerateSlotsMutation = useMutation({
    mutationFn: async ({ eventId, eventData }: { eventId: string; eventData: EventFormData }) => {
      // Delete only empty slots (those without registrations) for this event
      const { data: eventSlots, error: slotsFetchError } = await supabase
        .from('signup_slots')
        .select('id, signup_registrations ( id )')
        .eq('event_id', eventId);

      if (slotsFetchError) throw slotsFetchError;

      const emptySlotIds = (eventSlots || [])
        .filter((slot: { id: string; signup_registrations?: Array<{ id: string }> }) =>
          !slot.signup_registrations || slot.signup_registrations.length === 0
        )
        .map((slot: { id: string }) => slot.id);

      if (emptySlotIds.length > 0) {
        const { error: deleteError } = await supabase
          .from('signup_slots')
          .delete()
          .in('id', emptySlotIds);
        if (deleteError) throw deleteError;
      }

      // Generate new slots for the event
      const slots = buildSlotsForEvent(eventId, eventData);

      // Insert new slots (ignore conflicts with existing slots that have registrations)
      if (slots.length > 0) {
        const { error: slotsError } = await supabase
          .from('signup_slots')
          .upsert(slots, { onConflict: 'event_id,slot_date,start_time', ignoreDuplicates: true });
        
        if (slotsError) throw slotsError;
      }
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signup-events'] });
      queryClient.invalidateQueries({ queryKey: ['signup-slots', editingEventId] });
      trackEvent({
        eventType: 'admin_signup_event_regenerate_slots',
        user: currentUser,
        userType: 'admin',
        entityType: 'signup_events'
      });
    },
  });

  // Activate / deactivate an event. This is the switch that decides whether parents can see
  // the round at all, so it is one click from the list rather than a field buried in the edit
  // form — building a round inactive and turning it on later is the normal way to run one.
  const toggleEventActiveMutation = useMutation({
    mutationFn: async ({ eventId, isActive }: { eventId: string; isActive: boolean }) => {
      const { error } = await supabase
        .from('signup_events')
        .update({ is_active: isActive })
        .eq('id', eventId);

      if (error) throw error;

      trackEvent({
        eventType: 'admin_signup_event_edit',
        user: currentUser,
        userType: 'admin',
        entityType: 'signup_events',
        entityId: eventId,
        metadata: { action: isActive ? 'activate' : 'deactivate' },
      });
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signup-events'] });
      queryClient.invalidateQueries({ queryKey: ['signup-events-for-calendar'] });
      queryClient.invalidateQueries({ queryKey: ['has-signup-event-admin'] });
    },
  });

  // Duplicate event mutation
  const duplicateEventMutation = useMutation({
    mutationFn: async (originalEvent: SignupEvent) => {
      // Create new event with "Copy of" prefix
      const { data: newEvent, error: eventError } = await supabase
        .from('signup_events')
        .insert({
          title: `Copy of ${originalEvent.title}`,
          description: originalEvent.description,
          start_date: originalEvent.start_date,
          end_date: originalEvent.end_date,
          slot_duration_minutes: originalEvent.slot_duration_minutes,
          max_signups_per_slot: originalEvent.max_signups_per_slot,
          target_grades: originalEvent.target_grades,
          lock_hours_before: originalEvent.lock_hours_before,
          is_active: false, // Start as inactive
        })
        .select()
        .single();

      if (eventError) throw eventError;

      // Get original slots (without registrations data)
      const { data: originalSlots, error: slotsError } = await supabase
        .from('signup_slots')
        .select('slot_date, start_time, end_time, meeting_mode')
        .eq('event_id', originalEvent.id);

      if (slotsError) throw slotsError;

      // Create new slots for the duplicated event
      if (originalSlots && originalSlots.length > 0) {
        const newSlots = originalSlots.map(slot => ({
          event_id: newEvent.id,
          slot_date: slot.slot_date,
          start_time: slot.start_time,
          end_time: slot.end_time,
          meeting_mode: normalizeMeetingMode(slot.meeting_mode),
          is_available: true,
        }));

        const { error: insertSlotsError } = await supabase
          .from('signup_slots')
          .insert(newSlots);

        if (insertSlotsError) throw insertSlotsError;
      }

      return newEvent;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signup-events'] });
      trackEvent({
        eventType: 'admin_signup_event_duplicate',
        user: currentUser,
        userType: 'admin',
        entityType: 'signup_events'
      });
    },
  });



  const generatePublicLink = (eventId: string) => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/signup/${eventId}`;
  };

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    alert('Link copied to clipboard!');
  };

  const handleCreateEvent = (e: React.FormEvent) => {
    e.preventDefault();
    createEventMutation.mutate(formData);
  };

  const handleUpdateEventMetadata = (e: React.FormEvent) => {
    e.preventDefault();
    if (editingEventId) {
      updateEventMetadataMutation.mutate({ eventId: editingEventId, eventData: formData });
    }
  };

  const handleRegenerateSlots = () => {
    if (!editingEventId || isLoadingEventSlots) return;

    const planned = slotsFromSchedule(formData);
    if (planned.length === 0) {
      // The destructive shape of this button: it deletes every empty slot first. Saying so is
      // the difference between "regenerate" and "delete all unbooked times".
      if (!window.confirm(
        'The schedule above produces no slots.\n\nRegenerating will delete every slot that nobody has booked and create nothing in their place. Continue?'
      )) {
        return;
      }
    }

    regenerateSlotsMutation.mutate({ eventId: editingEventId, eventData: formData });
  };

  const handleToggleEventActive = (event: SignupEvent) => {
    const message = event.is_active
      ? `Deactivate "${event.title}"?\n\nParents will no longer see it and cannot book a slot. Existing registrations are kept.`
      : `Activate "${event.title}"?\n\nParents will be able to see it and book a slot straight away.`;

    if (window.confirm(message)) {
      toggleEventActiveMutation.mutate({ eventId: event.id, isActive: !event.is_active });
    }
  };

  const handleDuplicateEvent = (event: SignupEvent) => {
    if (window.confirm(`Are you sure you want to duplicate "${event.title}"? This will create a copy with all the same time slots but no registrations.`)) {
      duplicateEventMutation.mutate(event);
    }
  };

  // Delete event mutation
  const deleteEventMutation = useMutation({
    mutationFn: async (eventId: string) => {
      // Delete the event - this will cascade to slots and registrations due to foreign key constraints
      const { error } = await supabase
        .from('signup_events')
        .delete()
        .eq('id', eventId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['signup-events'] });
      trackEvent({
        eventType: 'admin_signup_event_delete',
        user: currentUser,
        userType: 'admin',
        entityType: 'signup_events'
      });
    },
  });

  const handleDeleteEvent = (event: SignupEvent & { registration_count?: number }) => {
    const registrationCount = event.registration_count || 0;
    const confirmMessage = registrationCount > 0 
      ? `Are you sure you want to delete "${event.title}"?\n\nThis will permanently delete:\n• The event\n• All time slots\n• All ${registrationCount} registrations\n\nThis action cannot be undone.`
      : `Are you sure you want to delete "${event.title}"?\n\nThis will permanently delete the event and all its time slots.\n\nThis action cannot be undone.`;
    
    if (window.confirm(confirmMessage)) {
      deleteEventMutation.mutate(event.id);
    }
  };

  const openEditModal = (event: SignupEvent) => {
    setFormData({
      ...EMPTY_EVENT_FORM,
      title: event.title,
      description: event.description || '',
      startDate: event.start_date,
      endDate: event.end_date,
      slotDurationMinutes: event.slot_duration_minutes,
      slotIntervalMinutes: event.slot_duration_minutes,
      maxSignupsPerSlot: event.max_signups_per_slot,
      targetGrades: event.target_grades ?? [],
      lockEnabled: event.lock_hours_before != null,
      lockHoursBefore: event.lock_hours_before ?? DEFAULT_LOCK_HOURS,
      // Both are filled in from the event's existing slots below.
      morningStartTime: '',
      morningEndTime: '',
      eveningStartTime: '',
      eveningEndTime: '',
      customSlots: [],
      isActive: event.is_active,
    });
    setEditingEventId(event.id);
    setShowEditModal(event.id);
    setIsLoadingEventSlots(true);

    // Load the event's real slots. They feed two things: the morning/evening blocks (inferred
    // from the first day, so the simple repeat-daily editor still works), and the per-day list,
    // so switching to "custom times per day" shows what is actually booked rather than a guess.
    // Stored TIME values are business-local clock times; nothing here parses them as dates.
    (async () => {
      const { data: slots, error } = await supabase
        .from('signup_slots')
        .select('slot_date,start_time,end_time,meeting_mode')
        .eq('event_id', event.id)
        .order('slot_date', { ascending: true })
        .order('start_time', { ascending: true });

      if (error || !slots || slots.length === 0) {
        // Fallback to prior defaults if we can't infer.
        setFormData((prev) => ({
          ...prev,
          morningStartTime: prev.morningStartTime || '09:30',
          morningEndTime: prev.morningEndTime || '11:30',
          eveningStartTime: prev.eveningStartTime || '17:30',
          eveningEndTime: prev.eveningEndTime || '18:30',
        }));
        setIsLoadingEventSlots(false);
        return;
      }

      const typedSlots = slots as SlotTimeRow[];
      const firstDate = typedSlots[0].slot_date;
      const firstDaySlots = typedSlots.filter((s) => s.slot_date === firstDate);
      const inferred = inferTimeBlocksFromDaySlots(firstDaySlots, event.slot_duration_minutes);

      const existingDrafts: SlotDraft[] = typedSlots.map((slot) => ({
        date: slot.slot_date,
        startTime: slot.start_time,
        endTime: slot.end_time,
        meetingMode: normalizeMeetingMode(slot.meeting_mode),
      }));

      setFormData((prev) => ({
        ...prev,
        morningStartTime: inferred.morningStartTime || '09:30',
        morningEndTime: inferred.morningEndTime || '11:30',
        morningMeetingMode: inferred.morningMeetingMode,
        eveningStartTime: inferred.eveningStartTime || '',
        eveningEndTime: inferred.eveningEndTime || '',
        eveningMeetingMode: inferred.eveningMeetingMode,
        customSlots: existingDrafts,
      }));
      setIsLoadingEventSlots(false);
    })();
  };

  const resetForm = () => {
    setFormData(EMPTY_EVENT_FORM);
  };

  if (eventsLoading) {
    return (
      <AdminLayout>
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </AdminLayout>
    );
  }

  return (
    <AdminLayout>
      {/* The desktop shell is h-screen/overflow-hidden and hands each page a flex child, so a
          page that does not declare its own scroll container simply overflows and cannot be
          scrolled. Unprefixed rather than `lg:` on purpose: the shell switches to its desktop
          layout at 768px but `lg:` starts at 1024, which would leave a dead band in between
          where nothing scrolls. Matches AdminOverview. */}
      <div className="space-y-6 flex-1 min-h-0 overflow-y-auto">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Meeting Signups</h1>
            <p className="text-gray-600">Manage signup events and view registrations</p>
          </div>
          <div className="flex items-center gap-3">
            <label className="inline-flex items-center gap-2 text-sm text-gray-700">
              <input
                type="checkbox"
                checked={includeInactiveEvents}
                onChange={(e) => setIncludeInactiveEvents(e.target.checked)}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              Show inactive
            </label>
            <button
              onClick={() => {
                resetForm();
                setShowCreateModal(true);
              }}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700"
            >
              Create
            </button>
          </div>
        </div>

        {/* Events List */}
        <div className="bg-white shadow rounded-lg">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Signup Events</h2>
          </div>
          <div className="p-6">
            {events.length === 0 ? (
              <p className="text-gray-500 text-center py-8">
                {includeInactiveEvents ? 'No signup events found.' : 'No active signup events found.'}
              </p>
            ) : (
              <div className="space-y-4">
                {events.map((event) => (
                  <div
                    key={event.id}
                    onClick={() => setSelectedEventId(selectedEventId === event.id ? null : event.id)}
                    className={`border rounded-lg p-4 transition-colors cursor-pointer ${
                      selectedEventId === event.id
                        ? 'border-blue-300 bg-blue-50'
                        : 'border-gray-200 hover:bg-gray-50'
                    }`}
                  >
                    <div className="flex-1">
                      <div className="flex items-center space-x-2">
                        <h3 className="font-medium text-gray-900">{event.title}</h3>
                        <span
                          className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
                            event.is_active
                              ? 'bg-green-100 text-green-800'
                              : 'bg-gray-100 text-gray-800'
                          }`}
                        >
                          {event.is_active ? 'Active' : 'Inactive'}
                        </span>
                      </div>
                      {event.description && (
                        <p className="text-gray-600 text-sm mt-1 whitespace-pre-line">{event.description}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-4 mt-2 text-sm text-gray-500">
                        <div className="flex items-center">
                          <Calendar className="h-4 w-4 mr-1" />
                          {formatDateDisplay(event.start_date)} - {formatDateDisplay(event.end_date)}
                        </div>
                        <div className="flex items-center">
                          <Clock className="h-4 w-4 mr-1" />
                          {event.slot_duration_minutes} min slots
                        </div>
                        <div className="flex items-center">
                          <Users className="h-4 w-4 mr-1" />
                          Limited to {event.max_signups_per_slot} per slot
                        </div>
                        <div className="flex items-center">
                          <BarChart className="h-4 w-4 mr-1" />
                          {event.registration_count || 0} registrations
                        </div>
                        {event.meeting_modes.length > 0 && (
                          <div className="flex items-center">
                            <Video className="h-4 w-4 mr-1" />
                            {event.meeting_modes.map(meetingModeLabel).join(' + ')}
                          </div>
                        )}
                        <div className="flex items-center">
                          <Target className="h-4 w-4 mr-1" />
                          {event.target_grades?.length
                            ? event.target_grades.join(', ')
                            : 'All grades'}
                        </div>
                        <div className="flex items-center">
                          <Lock className="h-4 w-4 mr-1" />
                          {event.lock_hours_before != null
                            ? `Locks ${describeLockWindow(event.lock_hours_before)} before`
                            : 'No lock'}
                        </div>
                      </div>
                    </div>

                    {selectedEventId === event.id && (
                      <div className="flex flex-wrap gap-2 mt-3 pt-3 border-t border-blue-200">
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleToggleEventActive(event);
                          }}
                          disabled={toggleEventActiveMutation.isPending}
                          title={event.is_active
                            ? 'Hide this event from parents. Registrations are kept.'
                            : 'Make this event visible to parents so they can book a slot.'}
                          className={`inline-flex items-center px-2 py-1 text-xs font-medium rounded-md transition-colors disabled:opacity-50 ${
                            event.is_active
                              ? 'text-red-600 bg-red-50 border border-red-200 hover:bg-red-100 hover:text-red-900'
                              : 'text-green-600 bg-green-50 border border-green-200 hover:bg-green-100 hover:text-green-900'
                          }`}
                        >
                          {event.is_active ? <PowerOff className="h-3 w-3 mr-1" /> : <Power className="h-3 w-3 mr-1" />}
                          {event.is_active ? 'Deactivate' : 'Activate'}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            navigate(`/admin-app/signups/${event.id}`);
                          }}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-blue-600 bg-blue-50 border border-blue-200 rounded-md hover:bg-blue-100 hover:text-blue-900 transition-colors"
                          title="View registration details"
                        >
                          <BarChart className="h-3 w-3 mr-1" />
                          View Details
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            openEditModal(event);
                          }}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                          title="Edit event"
                        >
                          <Edit className="h-3 w-3 mr-1" />
                          Edit
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDuplicateEvent(event);
                          }}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-purple-600 bg-purple-50 border border-purple-200 rounded-md hover:bg-purple-100 hover:text-purple-900 transition-colors disabled:opacity-50"
                          title="Duplicate event"
                          disabled={duplicateEventMutation.isPending}
                        >
                          <Copy className="h-3 w-3 mr-1" />
                          {duplicateEventMutation.isPending ? 'Copying...' : 'Duplicate'}
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            copyToClipboard(generatePublicLink(event.id));
                          }}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 transition-colors"
                        >
                          <ExternalLink className="h-3 w-3 mr-1" />
                          Copy Link
                        </button>
                        <button
                          onClick={(e) => {
                            e.stopPropagation();
                            handleDeleteEvent(event);
                          }}
                          className="inline-flex items-center px-2 py-1 text-xs font-medium text-red-600 bg-red-50 border border-red-200 rounded-md hover:bg-red-100 hover:text-red-900 transition-colors disabled:opacity-50"
                          title="Delete event and all registrations"
                          disabled={deleteEventMutation.isPending}
                        >
                          <Trash2 className="h-3 w-3 mr-1" />
                          {deleteEventMutation.isPending ? 'Deleting...' : 'Delete'}
                        </button>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Calendar View */}
        <div className="bg-white shadow rounded-lg mt-6">
          <div className="px-6 py-4 border-b border-gray-200">
            <h2 className="text-lg font-medium text-gray-900">Calendar View</h2>
            <p className="text-gray-600 mt-1">Visual representation of all signup events and slots</p>
          </div>
          <div className="p-6">
            <SignupCalendar includeInactiveEvents={includeInactiveEvents} />
          </div>
        </div>

        {/* Create Event Modal */}
        {showCreateModal && (
          <Modal
            isOpen={showCreateModal}
            onClose={() => setShowCreateModal(false)}
            title="Create Signup Event"
          >
            <form onSubmit={handleCreateEvent} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Parent Teacher Meeting"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Brief description of the event"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slot Duration (minutes) *
                  </label>
                  <select
                    value={formData.slotDurationMinutes}
                    onChange={(e) => {
                      const slotDurationMinutes = parseInt(e.target.value);
                      // The cadence follows the duration until the admin sets it apart, so the
                      // default stays "back-to-back slots" without a second decision.
                      patchForm({
                        slotDurationMinutes,
                        ...(formData.slotIntervalMinutes === formData.slotDurationMinutes
                          ? { slotIntervalMinutes: slotDurationMinutes }
                          : {}),
                      });
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                    <option value={45}>45 minutes</option>
                    <option value={60}>60 minutes</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Limited to Signups per Slot *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    required
                    value={formData.maxSignupsPerSlot}
                    onChange={(e) => setFormData({ ...formData, maxSignupsPerSlot: parseInt(e.target.value) })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <SignupAudienceEditor
                targetGrades={formData.targetGrades}
                onChange={patchForm}
              />

              <SignupLockEditor
                lockEnabled={formData.lockEnabled}
                lockHoursBefore={formData.lockHoursBefore}
                onChange={patchForm}
                idPrefix="create"
              />

              <SignupScheduleEditor
                value={formData}
                onChange={patchForm}
                idPrefix="create"
              />

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActive"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                  Event is active and open for signups
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={createEventMutation.isPending}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {createEventMutation.isPending ? 'Creating...' : 'Create Event'}
                </button>
              </div>
            </form>
          </Modal>
        )}

        {/* Edit Event Modal */}
        {showEditModal && (
          <Modal
            isOpen={!!showEditModal}
            onClose={() => {
              setShowEditModal(null);
              setEditingEventId(null);
              resetForm();
            }}
            title="Edit Signup Event"
          >
            <form onSubmit={handleUpdateEventMetadata} className="space-y-4">
              <div className="bg-yellow-50 border border-yellow-200 rounded-md p-3 mb-4">
                <p className="text-sm text-yellow-800">
                  <strong>Note:</strong> Saving metadata does not change slots. Use "Regenerate Slots" to update empty slots while preserving slots with registrations — a slot someone has already booked keeps its time and how it is held.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Event Title *
                </label>
                <input
                  type="text"
                  required
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="e.g., Parent Teacher Meeting"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Description
                </label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                  className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  rows={3}
                  placeholder="Brief description of the event"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Start Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.startDate}
                    onChange={(e) => setFormData({ ...formData, startDate: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    End Date *
                  </label>
                  <input
                    type="date"
                    required
                    value={formData.endDate}
                    onChange={(e) => setFormData({ ...formData, endDate: e.target.value })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Slot Duration (minutes) *
                  </label>
                  <select
                    value={formData.slotDurationMinutes}
                    onChange={(e) => {
                      const slotDurationMinutes = parseInt(e.target.value);
                      // The cadence follows the duration until the admin sets it apart, so the
                      // default stays "back-to-back slots" without a second decision.
                      patchForm({
                        slotDurationMinutes,
                        ...(formData.slotIntervalMinutes === formData.slotDurationMinutes
                          ? { slotIntervalMinutes: slotDurationMinutes }
                          : {}),
                      });
                    }}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  >
                    <option value={15}>15 minutes</option>
                    <option value={30}>30 minutes</option>
                    <option value={45}>45 minutes</option>
                    <option value={60}>60 minutes</option>
                  </select>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Limited to Signups per Slot *
                  </label>
                  <input
                    type="number"
                    min="1"
                    max="10"
                    required
                    value={formData.maxSignupsPerSlot}
                    onChange={(e) => setFormData({ ...formData, maxSignupsPerSlot: parseInt(e.target.value) })}
                    className="w-full border border-gray-300 rounded-md px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <SignupAudienceEditor
                targetGrades={formData.targetGrades}
                onChange={patchForm}
              />

              <SignupLockEditor
                lockEnabled={formData.lockEnabled}
                lockHoursBefore={formData.lockHoursBefore}
                onChange={patchForm}
                idPrefix="edit"
              />

              <SignupScheduleEditor
                value={formData}
                onChange={patchForm}
                idPrefix="edit"
              />

              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="isActiveEdit"
                  checked={formData.isActive}
                  onChange={(e) => setFormData({ ...formData, isActive: e.target.checked })}
                  className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <label htmlFor="isActiveEdit" className="ml-2 block text-sm text-gray-700">
                  Event is active and open for signups
                </label>
              </div>

              <div className="flex justify-end space-x-3 pt-4">
                <button
                  type="button"
                  onClick={() => {
                    setShowEditModal(null);
                    setEditingEventId(null);
                    resetForm();
                  }}
                  className="px-4 py-2 border border-gray-300 rounded-md text-sm font-medium text-gray-700 bg-white hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={updateEventMetadataMutation.isPending || regenerateSlotsMutation.isPending}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50"
                >
                  {updateEventMetadataMutation.isPending ? 'Saving...' : 'Save Metadata'}
                </button>
                <button
                  type="button"
                  onClick={handleRegenerateSlots}
                  disabled={updateEventMetadataMutation.isPending || regenerateSlotsMutation.isPending || !editingEventId || isLoadingEventSlots}
                  className="px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-amber-600 hover:bg-amber-700 disabled:opacity-50"
                >
                  {regenerateSlotsMutation.isPending ? 'Regenerating...' : isLoadingEventSlots ? 'Loading slots...' : 'Regenerate Slots'}
                </button>
              </div>
            </form>
          </Modal>
        )}
      </div>
    </AdminLayout>
  );
}
